import type {
  CareerExplorerPeekProps,
  CareerExplorerTrajectoryStep,
} from '@highereducation/hen-ui';

import type { CareerExplorerDataset } from '../types';

const TRAJECTORY_EYEBROWS = ['Start', 'Next', 'Later'] as const;

const DEFAULT_TITLE_TEMPLATE = 'A peek into {career}';
const DEFAULT_SKILLS_HEADING = 'Core skills';
const DEFAULT_TRAJECTORY_TITLE = 'Where this leads';
const DEFAULT_TRAJECTORY_DESCRIPTION = 'Typical next steps on this path.';
const DEFAULT_COLLAPSED_LABEL = 'Show more about this role';
const DEFAULT_EXPANDED_LABEL = 'Show less';

export type CareerPeekCopy = {
  titleTemplate?: string;
  skillsHeading?: string;
  trajectoryTitle?: string;
  trajectoryDescription?: string;
  collapsedLabel?: string;
  expandedLabel?: string;
};

const authored = (value: string | undefined, fallback: string): string => {
  if (typeof value !== 'string') return fallback;

  const trimmed = value.trim();

  return trimmed ? trimmed : fallback;
};

const peekTitle = (template: string | undefined, careerLabel: string): string =>
  authored(template, DEFAULT_TITLE_TEMPLATE).replaceAll(
    '{career}',
    () => careerLabel,
  );

export function mapCareerPeek(
  dataset: CareerExplorerDataset,
  careerLabel: string,
  copy: CareerPeekCopy = {},
): CareerExplorerPeekProps {
  const steps: CareerExplorerTrajectoryStep[] = dataset.trajectory.map(
    (step, index) => ({
      id: step.id,
      eyebrow:
        TRAJECTORY_EYEBROWS[Math.min(index, TRAJECTORY_EYEBROWS.length - 1)],
      title: step.title,
      value: step.description,
    }),
  );

  return {
    emptyState: {
      description:
        'Once you choose a career, salary, open roles, and recommended programs appear here.',
      icon: 'search',
      title: 'Choose a career to see the details',
    },
    expandToggle: {
      collapsedLabel: authored(copy.collapsedLabel, DEFAULT_COLLAPSED_LABEL),
      expandedLabel: authored(copy.expandedLabel, DEFAULT_EXPANDED_LABEL),
    },
    isEmpty: false,
    skills: dataset.skills.map((skill) => ({
      id: skill.id,
      label: skill.label,
    })),
    skillsHeading: authored(copy.skillsHeading, DEFAULT_SKILLS_HEADING),
    statistics: [
      {
        id: 'average-salary',
        title: 'Average salary',
        value: dataset.peek.averageSalary,
        caption: 'Typical earnings',
      },
      {
        id: 'open-roles',
        title: 'Open roles',
        value: dataset.peek.openRoles,
        caption: 'Current openings',
      },
      {
        id: 'career-outlook',
        title: 'Career outlook',
        value: dataset.peek.careerOutlook,
        caption: 'Projected growth',
      },
    ],
    title: peekTitle(copy.titleTemplate, careerLabel),
    trajectory: {
      currentStepId: steps[0]?.id,
      description: authored(
        copy.trajectoryDescription,
        DEFAULT_TRAJECTORY_DESCRIPTION,
      ),
      icon: 'route',
      title: authored(copy.trajectoryTitle, DEFAULT_TRAJECTORY_TITLE),
      steps,
    },
  };
}
